// Session tally of which line items have COMPLETED a Part 1 match search, keyed by NAME + UOM. Drives
// the match-progress toast in the sticky header. Purely observational — nothing here touches the match
// pipeline. The specifics (key choice, globalThis, coalescing, what "completed" means) are documented
// inline next to the code they apply to, below.
import { registerNsbEventResetter } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbEventReset';

interface MatchProgressState {
  completedKeys: Set<string>;
  flushScheduled: boolean;
  subscribers: Set<() => void>;
  version: number;
}

// State lives on `globalThis` (not module scope) so it survives Next.js Fast Refresh: editing this file
// (or ItemIntelLink) hot-swaps the module, which would otherwise leave the persistent header subscribed
// to one instance while writes land on another. In production (no Fast Refresh) it is a plain singleton.
const globalRef = globalThis as unknown as { __arkestroMatchProgress?: MatchProgressState };
if (!globalRef.__arkestroMatchProgress) {
  globalRef.__arkestroMatchProgress = {
    completedKeys: new Set<string>(),
    flushScheduled: false,
    subscribers: new Set<() => void>(),
    version: 0,
  };
}
const store: MatchProgressState = globalRef.__arkestroMatchProgress;

// The stable identity of a match: trimmed name + trimmed UOM. Both the writer (ItemIntelLink) and the
// toast's denominator (StickyHeader) build keys through this, so their formatting can never drift.
// WHY name+UOM and NOT the line-item id: an upload persists the event and reloads its line items with
// fresh database ids while the item COUNT is unchanged, so an id-keyed tally would strand against the
// new ids and read 0 until a full refresh. Name+UOM is invariant across that save/reload (and is the
// same key ItemIntelLinkRenderer/api.ts uses for its match cache).
export const matchProgressKey = (
  name: string | null | undefined,
  uom: string | null | undefined,
): string => `${(name ?? '').trim()}|${(uom ?? '').trim()}`;

// A monotonically increasing version so useSyncExternalStore has a stable primitive snapshot to read.
const flush = (): void => {
  store.flushScheduled = false;
  store.version += 1;
  store.subscribers.forEach(fn => fn());
};

// Notifications are COALESCED onto a microtask so a burst of rows resolving from one batch response
// repaints subscribers once, not once per row.
const scheduleNotify = (): void => {
  if (store.flushScheduled) return;
  store.flushScheduled = true;
  queueMicrotask(flush);
};

// Record that a row's match has CONCLUDED — a match, a genuine no-match (0 candidates), or a failed
// search that exhausted its retries. Keyed by name+UOM; idempotent; a nameless row (nothing to search)
// is ignored. Only schedules a repaint when the set actually grows.
export const markMatchComplete = (
  name: string | null | undefined,
  uom: string | null | undefined,
): void => {
  if (!(name ?? '').trim()) return;
  const key = matchProgressKey(name, uom);
  if (store.completedKeys.has(key)) return;
  store.completedKeys.add(key);
  scheduleNotify();
};

// How many of the given match keys have completed. The caller passes one key per searchable line item
// (duplicates included), so items sharing a name+UOM each count once their shared key completes.
export const countCompletedAmong = (keys: string[]): number =>
  keys.reduce((count, key) => (store.completedKeys.has(key) ? count + 1 : count), 0);

export const getMatchProgressVersion = (): number => store.version;

export const subscribeMatchProgress = (fn: () => void): (() => void) => {
  store.subscribers.add(fn);
  return () => { store.subscribers.delete(fn); };
};

// Wipe the tally so the count re-climbs from zero — used both on an event change (a new event
// re-counts from scratch) and when the buyer restarts the background match sweep.
/**
 * Whether the buyer has asked for matching on this event yet.
 *
 * Rows match themselves as they are drawn, which is how ~120 of a large event resolved on upload
 * before the buyer pressed anything — the links appeared, the tally climbed, and the Get button was
 * offering work that had already partly happened. With a manual trigger the search has to wait for
 * the ask, in BOTH flavours: item intel alone and item intel + Smart Baseline.
 *
 * Cleared on every event change with the rest of this store, so arriving at an event always starts
 * from "nothing asked for".
 */
let matchingRequested = false;

export const isMatchingRequested = (): boolean => matchingRequested;

export const markMatchingRequested = (): void => {
  if (matchingRequested) return;
  matchingRequested = true;
  scheduleNotify();
};

/**
 * Counts uploads on the line-items page so the sweep can start over on each one; called by both upload
 * paths, replace and add.
 *
 * A counter rather than a boolean because the sweep keys an effect on it: a replace bringing the same row
 * count is still a fresh value, so it still resets. Not cleared by resetMatchProgress — it counts events
 * that happened, not state about the current event.
 */
let uploadGeneration = 0;

export const getUploadGeneration = (): number => uploadGeneration;

export const markUploadArrived = (): void => {
  uploadGeneration += 1;
  scheduleNotify();
};

export const resetMatchProgress = (): void => {
  store.completedKeys.clear();
  matchingRequested = false;
  scheduleNotify();
};

// Cleared on every event change (a new event re-counts from scratch), like seeIntelStore.
registerNsbEventResetter(resetMatchProgress);
